/**
 * zone-resolution.ts — resolve a visitor's home zone from their email, silently (ADR-120
 * Amendment 4).
 *
 * ⭐ THE SHAPE JEFF CHOSE, AND THE ONE HE REJECTED. The rejected design was a ranked chain of
 * signals — explicit override → remembered cookie → domain hint → geo → default. A ranked
 * guess is still a guess, and geo in particular routes a physically-travelling customer to the
 * wrong zone, which is the exact failure the question existed to solve. So resolution uses the
 * one signal that travels WITH the person rather than around them: the email they are already
 * typing into the sign-in form.
 *
 * ⭐⭐ AND IT IS NOT A SECOND SCREEN. `portal-subscriber` authenticates through Amplify's
 * programmatic `signIn()`, not a Cognito Hosted UI redirect — there is no hosted page to
 * sequence around — so the email+password form stays exactly one screen. On submit, this runs
 * as a background call before `signIn()`, and the visitor never sees it as a step, a click, or
 * a spinner of its own.
 *
 * ⚠ THE EMAIL NEVER LEAVES THE BROWSER. It is hashed here, locally, and only the digest is
 * sent. The routing directory is the platform's one global component and is permitted to be
 * global solely because it holds nothing personal (ADR-099 §D11); an endpoint that accepted
 * the raw address would make the lookup itself a cross-zone PII flow, whatever the table
 * stored.
 */
import { ENV } from '../amplify-config';
import { BUILD_ZONE, setActiveZone, type ZoneConfig } from './zone-session';
import { rememberZones } from './zone-sessions';

export type { ZoneConfig };

/** How long the resolver may take before the sign-in proceeds without it. */
const RESOLVE_TIMEOUT_MS = 4000;

/**
 * `SHA-256("email:" + trim(lowercase(email)))`, hex.
 *
 * ⚠ THIS MUST STAY BYTE-FOR-BYTE IDENTICAL TO `identityHash('email', …)` IN
 * `lambdas/shared/src/region-routing.ts` — same prefix, same trim, same lowercase, same hex.
 * The writer hashes there and the reader hashes here, and the two never see each other's
 * code. If they diverge, every lookup misses, every visitor silently falls back to the
 * default zone, and nothing anywhere reports an error: a US customer would simply be told
 * their password is wrong. `zone-hash-parity.test.ts` pins the two implementations against
 * the same vectors precisely because nothing else would notice.
 */
export async function hashEmail(email: string): Promise<string> {
  const data = new TextEncoder().encode(`email:${email.trim().toLowerCase()}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Resolve the zone for `email` and make it this session's active zone.
 *
 * ⭐⭐ IT CANNOT FAIL THE SIGN-IN, AND THAT IS THE WHOLE RISK POSTURE OF THIS STAGE. Stage 6
 * puts new code on the live login path, where the failure mode is "nobody can sign in". So
 * every unhappy path here — a network error, a 5xx, a timeout, a browser with no
 * `crypto.subtle`, a malformed response — resolves to the BUILD zone and lets `signIn()`
 * proceed exactly as it did before this code existed. The resolver can only ever IMPROVE on
 * the build-time default; it can never withhold a sign-in.
 *
 * ⚠ WHICH MEANS A RESOLVER OUTAGE IS SILENT, AND IS SUPPOSED TO BE. While every customer is
 * in the default zone that degradation is invisible and harmless. The day a second zone holds
 * real customers, a resolver outage becomes "customers of the non-default zone cannot sign
 * in" — so the console warning below is the signal that matters, and `verify-zone.ts`'s
 * `/zone-config` check is what watches it from outside.
 */
export async function resolveZoneForEmail(email: string): Promise<ZoneConfig> {
  try {
    if (!globalThis.crypto?.subtle) throw new Error('no-subtle-crypto');
    const identityHash = await hashEmail(email);
    const res = await fetch(`${ENV.PORTAL_API_BASE_URL}/portal/zone-config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identityHash }),
      signal: AbortSignal.timeout(RESOLVE_TIMEOUT_MS),
    });
    if (!res.ok) throw new Error(`zone-config ${res.status}`);
    const cfg = (await res.json()) as Partial<ZoneConfig> & { zones?: unknown };
    // Q13 — every zone's identity, so sign-out can revoke a session in a zone this browser was
    // never pointed at (lib/zone-sessions.ts). Absent from an older resolver: harmless.
    rememberZones(cfg.zones);
    // A response missing either Cognito field would configure Amplify into a state where
    // sign-in cannot succeed and cannot say why. Treat it as no answer at all.
    if (!cfg.userPoolId || !cfg.userPoolClientId || !cfg.portalApiBaseUrl) {
      throw new Error('zone-config incomplete');
    }
    const resolved: ZoneConfig = {
      zone: cfg.zone ?? BUILD_ZONE.zone,
      region: cfg.region ?? '',
      userPoolId: cfg.userPoolId,
      userPoolClientId: cfg.userPoolClientId,
      portalApiBaseUrl: cfg.portalApiBaseUrl,
    };
    setActiveZone(resolved);
    return resolved;
  } catch (err) {
    console.warn('zone-resolution: falling back to the build zone', {
      reason: err instanceof Error ? err.message : String(err),
    });
    setActiveZone(BUILD_ZONE);
    return BUILD_ZONE;
  }
}
